/**
 * WebSocket Context
 *
 * Owns the single app-lifetime `/api/ws` connection, opened on the first
 * subscriber. Subscribers register a message (and optional error) callback
 * and receive every event broadcast on the shared socket; `useWebSocket` is
 * a thin subscriber on top of this.
 */

import {
  createContext,
  createSignal,
  useContext,
  onCleanup,
  type ParentComponent,
} from "solid-js";
import type { BoardEvent } from "../api/types";

/** WebSocket connection status */
export type WebSocketStatus = "connecting" | "connected" | "disconnected";

interface WebSocketContextValue {
  /** Register a subscriber; returns an unsubscribe function. */
  subscribe(
    onMessage: (event: BoardEvent) => void,
    onError?: (error: Event) => void,
  ): () => void;
  /** Current connection status. */
  status: () => WebSocketStatus;
}

interface Subscriber {
  onMessage: (event: BoardEvent) => void;
  onError?: (error: Event) => void;
}

const WebSocketContext = createContext<WebSocketContextValue>();

const INITIAL_RECONNECT_DELAY_MS = 1000;
const MAX_RECONNECT_DELAY_MS = 30000;

/**
 * How long a connection must stay open to count as "real", and so to earn a
 * reset of the reconnect backoff (FLOW-984).
 *
 * The server accepts the upgrade and *then* closes the socket in several
 * routine cases — a caller with zero project memberships, a membership
 * revocation, an unexpected inbound frame (see
 * `backend/src/handlers/websocket.rs`). The client sees `onopen` immediately
 * followed by `onclose`, so resetting the backoff on open pinned the delay at
 * 1s and reconnected ~60x/minute forever. Each of those reconnects also costs
 * a `GET /api/auth/me`, which lives under the strict per-IP auth limit
 * (10/min) and started answering 429. Gating the reset on connection lifetime
 * lets a flapping socket actually back off to `MAX_RECONNECT_DELAY_MS`, while
 * a connection that did useful work still reconnects promptly when it drops.
 *
 * The accepted cost: a caller with zero project memberships never holds a
 * connection open, so its backoff escalates to `MAX_RECONNECT_DELAY_MS` and
 * stays there. When an admin adds them to their first project, live updates
 * begin up to 30s later instead of ~1s — a one-time delay on an account that
 * by definition has nothing to receive yet, in exchange for not spending its
 * whole auth budget while it waits.
 */
const STABLE_CONNECTION_MS = 10000;

function getWsBaseUrl(): string {
  const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${protocol}//${window.location.host}`;
}

export const WebSocketProvider: ParentComponent = (props) => {
  const [status, setStatus] = createSignal<WebSocketStatus>("disconnected");
  const subscribers = new Set<Subscriber>();

  let ws: WebSocket | null = null;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let currentReconnectDelay = INITIAL_RECONNECT_DELAY_MS;
  let shouldReconnect = true;
  let openedAt: number | null = null;

  const connect = () => {
    setStatus("connecting");
    openedAt = null;

    try {
      ws = new WebSocket(`${getWsBaseUrl()}/api/ws`);

      ws.onopen = () => {
        setStatus("connected");
        openedAt = Date.now();
      };

      ws.onclose = () => {
        setStatus("disconnected");
        ws = null;

        // See STABLE_CONNECTION_MS: only a connection that stayed up long
        // enough to be real resets the backoff, so an accept-then-close flap
        // keeps escalating instead of retrying every second.
        if (
          openedAt !== null &&
          Date.now() - openedAt >= STABLE_CONNECTION_MS
        ) {
          currentReconnectDelay = INITIAL_RECONNECT_DELAY_MS;
        }
        openedAt = null;

        if (shouldReconnect) {
          reconnectTimer = setTimeout(() => {
            reconnectTimer = null;
            connect();
            currentReconnectDelay = Math.min(
              currentReconnectDelay * 2,
              MAX_RECONNECT_DELAY_MS,
            );
          }, currentReconnectDelay);
        }
      };

      ws.onmessage = (e) => {
        let event: BoardEvent;
        try {
          event = JSON.parse(e.data) as BoardEvent;
        } catch (err) {
          console.error("Failed to parse WebSocket message:", err);
          return;
        }

        for (const subscriber of subscribers) {
          try {
            subscriber.onMessage(event);
          } catch (err) {
            console.error("WebSocket subscriber threw:", err);
          }
        }
      };

      ws.onerror = (e) => {
        for (const subscriber of subscribers) {
          try {
            subscriber.onError?.(e);
          } catch (err) {
            console.error("WebSocket error handler threw:", err);
          }
        }
      };
    } catch (err) {
      console.error("Failed to create WebSocket:", err);
      setStatus("disconnected");
    }
  };

  onCleanup(() => {
    shouldReconnect = false;

    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }

    if (ws) {
      ws.close();
      ws = null;
    }
  });

  const value: WebSocketContextValue = {
    subscribe: (onMessage, onError) => {
      const subscriber: Subscriber = { onMessage, onError };
      subscribers.add(subscriber);
      // Connect lazily on the first subscriber so a public route (/, /login,
      // /terms, /privacy), which has no subscribers, never opens /api/ws.
      // Never closed on zero subscribers: every shell route mounts its own
      // AppShell, so closing would churn the socket on each navigation.
      if (shouldReconnect && !ws && !reconnectTimer) connect();
      return () => subscribers.delete(subscriber);
    },
    status,
  };

  return (
    <WebSocketContext.Provider value={value}>
      {props.children}
    </WebSocketContext.Provider>
  );
};

export function useWebSocketContext(): WebSocketContextValue {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error(
      "useWebSocketContext must be used within a WebSocketProvider",
    );
  }
  return context;
}
